/**
 * Bottom element switcher — the primary input path on the tablet (soft keyboards
 * have no Tab key). Two prominent quick buttons show the most likely next
 * elements; a scrollable row exposes every element. Tapping retypes the current
 * paragraph. Sits above the soft keyboard via --keyboard-inset.
 */
import type { StyleDef } from "../format/types";
import { nextSuggestions } from "../editor/flow";

/** Compact labels for the chips. */
const LABELS: Record<string, string> = {
  "Scene Heading": "Scene",
  Action: "Action",
  Character: "Char",
  Parenthetical: "( )",
  Dialogue: "Dialog",
  Transition: "Trans",
  Shot: "Shot",
  "Normal Text": "Normal",
};

/** Element order shown in the switcher (built-in script elements). */
const ORDER = [
  "Scene Heading",
  "Action",
  "Character",
  "Parenthetical",
  "Dialogue",
  "Transition",
  "Shot",
];

export interface ElementSwitcherProps {
  styles: Map<string, StyleDef>;
  current: string | null;
  onPick: (styleName: string) => void;
}

export function ElementSwitcher({ styles, current, onPick }: ElementSwitcherProps) {
  const elements = ORDER.filter((name) => styles.has(name));
  const quick = current ? nextSuggestions(styles, current) : ["Action", "Character"];

  // Prevent the buttons from stealing focus (keeps the editor caret + keyboard).
  const hold = (name: string) => (e: React.PointerEvent) => {
    e.preventDefault();
    onPick(name);
  };

  return (
    <nav className="sp-switcher" aria-label="Element switcher">
      <div className="sp-switcher-quick">
        {quick.map((name) => (
          <button
            key={name}
            className="sp-quick-btn"
            onPointerDown={hold(name)}
            title={`Next: ${name}`}
          >
            {LABELS[name] ?? name}
          </button>
        ))}
      </div>
      <div className="sp-switcher-all">
        {elements.map((name) => (
          <button
            key={name}
            className={"sp-elem-btn" + (name === current ? " sp-elem-current" : "")}
            onPointerDown={hold(name)}
            title={name}
          >
            {LABELS[name] ?? name}
          </button>
        ))}
      </div>
    </nav>
  );
}
